/*
TODO LIST
Add feature(s):
- 
Fix bug(s):
- 
Make change(s):
- 
Other:
- 
Requirements\rules:
- Do not remove anything from my code unless needed
- Do not refactor anything, only add the features, fix bugs or add changes
- Return only the code I need to change
- Cut code into pieces I can copy
*/
import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Select,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const DraggableTableCell = React.forwardRef(function DraggableTableCell(
  { isDragging, children, ...other },
  ref
) {
  return (
    <TableCell
      ref={ref}
      {...other}
      sx={{
        cursor: 'grab',
        userSelect: 'none',
        backgroundColor: (theme) =>
          isDragging
            ? theme.palette.action.hover
            : theme.palette.background.paper,
      }}
    >
      {children}
    </TableCell>
  );
});

const CSV = () => {
  const sampleData = `First Name,Last Name,Email,Age,Salary,Date of birth,Join date
John,Smith,john.smith@example.com,30,50000.75,01/15/1990,2020-01-01
Jane,Doe,jane.doe@example.com,25,45000.50,1990-02-20,15/02/2021
Michael,Johnson,michael.johnson@example.com,35,60000.00,March 10, 1990,2022.03.10
Sarah,Lee,sarah.lee@example.com,28,48000.25,1992-05-12,2019/05/15`;
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [csvText, setCsvText] = useState(sampleData);
  const [csvOutput, setCsvOutput] = useState('');
  const [outputDelimiter, setOutputDelimiter] = useState(','); // Default output delimiter: comma
  const [decimalSign, setDecimalSign] = useState('.'); // Default decimal sign: dot
  const [newColumnName, setNewColumnName] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [dateColumns, setDateColumns] = useState({}); // To store detected date columns
  const [dateFormats, setDateFormats] = useState({}); // To store selected date formats for date columns
  const [hiddenColumns, setHiddenColumns] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [addFieldDelimiters, setAddFieldDelimiters] = useState(false);
  const [openLibrariesModal, setOpenLibrariesModal] = useState(false);
  const [openChangelogModal, setOpenChangelogModal] = useState(false);
  const [openXmlDialog, setOpenXmlDialog] = useState(false); // State for the XML dialog
  const [xmlRoot, setXmlRoot] = useState('Contacts'); // XML root
  const [recordType, setRecordType] = useState('Contact'); // XML record type
  // New state to track the selected data types for columns
  const [columnDataTypes, setColumnDataTypes] = useState({});
  const [openDataTypeDialog, setOpenDataTypeDialog] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [openDateFormatDialog, setOpenDateFormatDialog] = useState(false); // State for the date format dialog

  // Handler for copying to clipboard
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(csvOutput).then(
      () => {
        alert('CSV output copied to clipboard!');
      },
      () => {
        alert('Failed to copy CSV output to clipboard.');
      }
    );
  };

  // Handlers for modals
  const handleOpenLibrariesModal = () => setOpenLibrariesModal(true);
  const handleCloseLibrariesModal = () => setOpenLibrariesModal(false);

  const handleOpenChangelogModal = () => setOpenChangelogModal(true);
  const handleCloseChangelogModal = () => setOpenChangelogModal(false);

  const handleCsvTextChange = (event) => {
    setCsvText(event.target.value);
  };

  const isDate = (value) => {
    const ddMmYyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/; // Specifically match DD/MM/YYYY
    const mmDdYyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/; // Match MM/DD/YYYY
    const yyyyMmDdPattern = /^\d{4}-\d{2}-\d{2}$/;   // Match YYYY-MM-DD

    return (
      ddMmYyyyPattern.test(value) ||
      yyyyMmDdPattern.test(value) ||
      mmDdYyyyPattern.test(value) ||
      !isNaN(Date.parse(value)) // Fallback to JavaScript Date parsing for other formats
    );
  };

  const handleParseCsvText = () => {
    Papa.parse(csvText, {
      header: true,
      delimiter: '', // Auto-detect delimiter for input parsing
      transform: (value) => value.trim(),
      complete: (results) => {
        const trimmedFields = results.meta.fields.map((field) => field.trim());
        const trimmedData = results.data
          .filter((row) => Object.keys(row).length > 0)
          .map((row) => {
            const newRow = {};
            trimmedFields.forEach((field) => {
              newRow[field] = row[field];
            });
            return newRow;
          });
  
        setData(trimmedData);  // Set data first
        setColumns(trimmedFields);
  
        // Call detectDataTypes after data and columns are set
        const newColumnDataTypes = {};
        trimmedData.forEach((row) => {
          trimmedFields.forEach((col) => {
            const value = row[col];
            if (!newColumnDataTypes[col]) {
              if (!isNaN(value) && value !== '') {
                newColumnDataTypes[col] = Number.isInteger(parseFloat(value)) ? 'integer' : 'float';
              } else if (isDate(value)) {
                newColumnDataTypes[col] = 'date';
              } else {
                newColumnDataTypes[col] = 'string';
              }
            }
          });
        });
        setColumnDataTypes(newColumnDataTypes);  // Set detected data types
      },
    });
  };  

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const newColumns = Array.from(columns);
    const [movedColumn] = newColumns.splice(source.index, 1);
    newColumns.splice(destination.index, 0, movedColumn);

    setColumns(newColumns);
  };

  const handleToggleVisibility = (index) => {
    const column = columns[index];
    setHiddenColumns((prev) => ({
      ...prev,
      [column]: !prev[column], // Toggle visibility for CSV output
    }));
  };

  // Add a column with default value
  const handleAddColumn = () => {
    if (newColumnName.trim()) {
      const updatedColumns = [...columns, newColumnName.trim()];
      const updatedData = data.map((row) => ({
        ...row,
        [newColumnName.trim()]: defaultValue,
      }));

      setColumns(updatedColumns);
      setData(updatedData);
      setNewColumnName('');
      setDefaultValue('');
    }
  };

  const handleOutputDelimiterChange = (event) => {
    setOutputDelimiter(event.target.value);
    if (event.target.value === ',' && decimalSign === ',') {
      setDecimalSign('.'); // Ensure decimal sign is dot when comma is delimiter
    }
  };

  const handleDecimalSignChange = (event) => {
    const newDecimalSign = event.target.value;
    if (outputDelimiter === ',' && newDecimalSign === ',') {
      alert("You can't set both delimiter and decimal sign to ','");
    } else {
      setDecimalSign(newDecimalSign);
    }
  };

  const handleConvertToJson = () => {
    const csvRows = Papa.parse(csvOutput, { header: true }).data;
    const jsonOutput = JSON.stringify(csvRows, null, 2); // Convert CSV data to JSON format
    setCsvOutput(jsonOutput); // Set csvOutput to the JSON string
  };

  const handleConvertToXml = () => {
    setOpenXmlDialog(true); // Open the XML prompt dialog
  };

  const handleCloseXmlDialog = () => {
    setOpenXmlDialog(false); // Close the XML dialog
  };

  // Function to open the data type dialog for a column
  const handleColumnClick = (col) => {
    setSelectedColumn(col);
    setOpenDataTypeDialog(true);
  };

  // Function to handle data type selection
  const handleDataTypeSelect = (col, dataType) => {
    setColumnDataTypes((prev) => ({
      ...prev,
      [col]: dataType,
    }));

    if (dataType === 'date') {
      // Open date format dialog if the user selects 'Date'
      setSelectedColumn(col);
      setOpenDateFormatDialog(true);
    } else {
      setOpenDataTypeDialog(false);
    }
  };

  const generateXmlFromData = () => {
    const parsedData = Papa.parse(csvOutput, { header: true }).data; // Parse csvOutput
    let xmlOutput = `<${xmlRoot}>\n`;
    parsedData.forEach((row) => {
      xmlOutput += `  <${recordType}>\n`;
      columns.forEach((col) => {
        xmlOutput += `    <${col}>${row[col]}</${col}>\n`;
      });
      xmlOutput += `  </${recordType}>\n`;
    });
    xmlOutput += `</${xmlRoot}>`;

    setCsvOutput(xmlOutput); // Set the XML output to the csvOutput state
    setOpenXmlDialog(false); // Close the XML dialog
  };

  const handleConvertToCsv = () => {
    handleParseCsvText(); // Re-parse the CSV data to go back to CSV format
  };

  const formatDate = (dateString, format, originalValue) => {
    const ddMmYyyyPattern = /^\d{2}\/\d{2}\/\d{4}$/; // Pattern for DD/MM/YYYY (e.g., 15/02/2021)

    let day, month, year;

    // Exception for DD/MM/YYYY format (common in Dutch/German locales)
    if (ddMmYyyyPattern.test(dateString)) {
      [day, month, year] = dateString.split('/'); // Manually parse DD/MM/YYYY
    }
    // Handle YYYY-MM-DD format (ISO format)
    else {
      const parsedDate = new Date(dateString);
      if (isNaN(parsedDate)) {
        return originalValue; // Return original value if the date is invalid
      }
      day = parsedDate.getDate().toString().padStart(2, '0');
      month = (parsedDate.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
      year = parsedDate.getFullYear().toString();
    }

    // Format the parsed date according to the requested format
    switch (format) {
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'MMM DD, YYYY':
        return new Date(`${year}-${month}-${day}`).toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        });
      default:
        return originalValue; // Return original value if format is "leave"
    }
  };

  // Call detectDataTypes function after parsing the CSV
  useEffect(() => {
    handleParseCsvText();
    // eslint-disable-next-line
  }, []);

  // Ensure default data types are set to 'text' when columns are initialized
  useEffect(() => {
    if (columns.length > 0) {
      setColumnDataTypes((prev) => {
        const updatedDataTypes = { ...prev };
        columns.forEach((col) => {
          if (!updatedDataTypes[col]) {
            updatedDataTypes[col] = 'string'; // Set default datatype to 'text'
          }
        });
        return updatedDataTypes;
      });
    }
  }, [columns]);

  useEffect(() => {
    if (data.length > 0 && columns.length > 0) {
      const reorderedData = data.map((row) => {
        const formattedRow = columns.reduce((acc, col) => {
          if (!hiddenColumns[col]) {
            let value = row[col];

            const dataType = columnDataTypes[col];

            if (dataType === 'date' && dateColumns[col]) {
              value = formatDate(value, dateFormats[col], row[col]); // Apply the selected date format
            } else if (dataType === 'number' || (!isNaN(parseFloat(value)) && isFinite(value))) {
              // Apply decimal sign formatting for numeric values
              value = value.toString().replace('.', decimalSign === ',' ? ',' : '.');
            }

            acc[col] = value;
          }
          return acc;
        }, {});

        return formattedRow;
      });

      // Generate CSV content from the formatted rows
      const csvContent = Papa.unparse(reorderedData, {
        delimiter: outputDelimiter,
        quotes: (value) => {
          // Only quote non-numeric values if addFieldDelimiters is true
          return addFieldDelimiters && isNaN(value);
        },
      });

      setCsvOutput(csvContent);
    } else {
      setCsvOutput('');
    }
  }, [data, columns, outputDelimiter, decimalSign, dateColumns, dateFormats, hiddenColumns, addFieldDelimiters, columnDataTypes]);

  // CSV file upload handler
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {

      const reader = new FileReader();
      reader.onload = (e) => {
        const csvText = e.target.result; // Get the file content
        setCsvText(csvText); // Set the file content (as text)
      };
      reader.readAsText(file); // Read file as text

      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const trimmedFields = results.meta.fields.map((field) => field.trim());
          const trimmedData = results.data
            .filter((row) => Object.keys(row).length > 0)
            .map((row) => {
              const newRow = {};
              trimmedFields.forEach((field) => {
                newRow[field] = row[field];
              });
              return newRow;
            });
          setData(trimmedData);
          setColumns(trimmedFields);
        },
      });
    }
  };

  const handleDownload = () => {
    let blob;
    let fileType;
    let fileName;

    try {
      // Try to parse the csvOutput to check if it's JSON
      JSON.parse(csvOutput);
      fileType = 'application/json;charset=utf-8;';
      fileName = 'data.json';
    } catch (e) {
      // If parsing fails, treat it as CSV
      fileType = 'text/csv;charset=utf-8;';
      fileName = 'reordered.csv';
    }

    blob = new Blob([csvOutput], { type: fileType });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSort = (column) => {
    const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortDirection(newDirection);

    const sortedData = [...data].sort((a, b) => {
      if (a[column] < b[column]) return newDirection === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return newDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setData(sortedData);
  };

  // Limit the number of records displayed in the table
  const MAX_ROWS = 10;

  return (
    <Container maxWidth="lg">
      <CssBaseline />
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>
          CSV column reordering tool
        </Typography>

        {/* Description of functionality */}
        <Typography variant="body1" sx={{ mt: 2 }}>
          Features: free as in beer, paste or upload CSV data, reorder columns by dragging and dropping, hide columns, change delimiter, modify the decimal sign, change date formats, add new columns and finally download or copy the updated CSV.
        </Typography>

        <Typography variant="body2" sx={{ mt: 2 }}>
          Experimental: convert to JSON, convert to XML, change data types, set date format.
        </Typography>

        {/* Privacy notice and disclaimer */}
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          <strong>Privacy notice:</strong> Your data is processed locally in your browser and is not sent to any server. This tool does not track you or display ads. Please note that I use TypeForm for feedback/bug reports, TypeForm does use first/third-party cookies.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <strong>Disclaimer:</strong> This tool is provided "as is" without any warranty. Use it at your own risk.
        </Typography>

        {/* Buttons for Modals */}
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleOpenLibrariesModal}
            sx={{ mr: 2 }}
          >
            License info
          </Button>

          <Button
            variant="outlined"
            onClick={handleOpenChangelogModal}
            sx={{ mr: 2 }}
          >
            Changelog
          </Button>

          <Button
            variant="outlined"
            href="https://donate.stripe.com/bIY28Y1pZ61z5Us6oo"
            target="_blank"
            sx={{ mr: 2 }}
          >
            Buy me coffee
          </Button>

          <Button
            variant="outlined"
            href="https://atkfnmze4gr.typeform.com/to/lViY4I7R"
            target="_blank"
          >
            Bug report/Feedback
          </Button>

        </Box>

        {/* CSV Paste Area */}
        <Typography variant="h6" sx={{ mt: 4 }}>
          Step 1: load CSV from file or paste it below and click on parse
        </Typography>

        {/* Upload CSV Section */}
        <Box sx={{ mt: 2 }}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
          />
        </Box>

        <TextField
          label=""
          multiline
          rows={6}
          variant="outlined"
          fullWidth
          value={csvText}
          onChange={handleCsvTextChange}
          sx={{ mt: 2 }}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={handleParseCsvText}
          sx={{ mt: 2 }}
        >
          Parse
        </Button>

        {data.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 4 }}>
              Step 2: reorder columns by dragging the headers, sort them or hide/unhide them by clicking visibility icon
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Experimental: click once on column names to change data type and\or date formats.
            </Typography>
            {/* Table */}
            <TableContainer
              component={Paper}
              sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
            >
              <Table>
                <TableHead>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="columns" direction="horizontal">
                      {(provided) => (
                        <TableRow
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {columns.map((col, index) => (
                            <Draggable
                              key={col}
                              draggableId={col}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <DraggableTableCell
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  isDragging={snapshot.isDragging}
                                  align="center"
                                  sx={{
                                    padding: '4px 8px', // Reduce padding
                                    fontSize: '0.800rem', // Smaller font size
                                  }}
                                >
                                  {/* Table header text */}
                                  <Typography variant='body2' onClick={() => handleColumnClick(col)}>
                                    {col}
                                  </Typography>
                                  <Typography variant='body2' onClick={() => handleColumnClick(col)}>
                                    [{columnDataTypes[col]}]
                                  </Typography>
                                  <IconButton size="small" onClick={() => handleSort(col)} sx={{ ml: 1 }}>
                                    {sortColumn === col && sortDirection === 'asc' ? '▲' : '▼'}
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleVisibility(index)}
                                    sx={{ ml: 1 }}
                                  >
                                    {hiddenColumns[col] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                  </IconButton>
                                </DraggableTableCell>

                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </TableRow>
                      )}
                    </Droppable>
                  </DragDropContext>
                </TableHead>
                <TableBody>
                  {data.slice(0, MAX_ROWS).map((row, rowIndex) => (
                    <TableRow key={`row-${rowIndex}`}>
                      {columns.map((col, colIndex) => (
                        <TableCell
                          key={`cell-${rowIndex}-${colIndex}`}
                          sx={{
                            padding: '4px 8px', // Reduce padding
                            fontSize: '0.875rem', // Smaller font size
                          }}
                        >
                          {row[col]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" color="textSecondary">
              Displaying the first {Math.min(data.length, MAX_ROWS)} of {data.length} records.
            </Typography>

            {Object.keys(dateColumns).length > 0 && (
              <>
                <Typography variant="h6" sx={{ mt: 4 }}>
                  Step 3: set date formats for date columns
                </Typography>
                {Object.keys(dateColumns).map((col) => (
                  <Box key={col} sx={{ mt: 2 }}>
                    <Typography variant="body1">{col}</Typography>
                    <Select
                      value={dateFormats[col]}
                      onChange={(e) => {
                        setDateFormats((prev) => ({
                          ...prev,
                          [col]: e.target.value,
                        }));
                      }}
                      sx={{ mt: 1 }}
                    >
                      <MenuItem value="leave">Do not change</MenuItem>
                      <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                      <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                      <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                      <MenuItem value="MMM DD, YYYY">MMM DD, YYYY</MenuItem>
                      <MenuItem value="DD-MM-YYYY">DD-MM-YYYY</MenuItem>
                      <MenuItem value="DD MMM YYYY">DD MMM YYYY</MenuItem>
                      <MenuItem value="MMMM DD, YYYY">MMMM DD, YYYY</MenuItem>
                    </Select>

                  </Box>
                ))}
              </>
            )}

            {/* Add new column with default value */}
            <Typography variant="h6" sx={{ mt: 4 }}>
              Step 4 (optionally): add a column with or without a value
            </Typography>
            <TextField
              label="New column name"
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              sx={{ mt: 2 }}
              fullWidth
            />
            <TextField
              label="Default value"
              value={defaultValue}
              onChange={(e) => setDefaultValue(e.target.value)}
              sx={{ mt: 2 }}
              fullWidth
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddColumn}
              sx={{ mt: 2 }}
            >
              Add column
            </Button>

            {/* Output Delimiter Selection */}
            <Typography variant="h6" sx={{ mt: 4 }}>
              Step 5: set delimiter
            </Typography>
            <Select
              value={outputDelimiter}
              onChange={handleOutputDelimiterChange}
              sx={{ mt: 2 }}
            >
              <MenuItem value=",">Comma (,)</MenuItem>
              <MenuItem value=";">Semicolon (;)</MenuItem>
              <MenuItem value="|">Pipe (|)</MenuItem>
              <MenuItem value="\t">Tab</MenuItem>
            </Select>

            {/* Decimal Sign Selection */}
            <Typography variant="h6" sx={{ mt: 4 }}>
              Step 6: set decimal sign
            </Typography>
            <Select
              value={decimalSign}
              onChange={handleDecimalSignChange}
              sx={{ mt: 2 }}
            >
              <MenuItem value=".">Dot (.)</MenuItem>
              <MenuItem value=",">Comma (,)</MenuItem>
            </Select>

            {/* CSV Output */}
            {csvOutput && (
              <>
                {/* Add new checkbox for wrapping non-integer/decimal fields */}
                <Typography variant="h6" sx={{ mt: 4 }}>
                  Step 7: add field delimiters for non-numeric fields
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <input
                    type="checkbox"
                    checked={addFieldDelimiters}
                    onChange={(e) => setAddFieldDelimiters(e.target.checked)}
                  />
                  <Typography sx={{ ml: 2 }}>
                    Add delimiters (e.g., quotes) to fields that are not integers or decimals
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ mt: 4 }}>
                  Step 8: copy or download result
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  Note: leading and trailing spaces are automatically trimmed.
                </Typography>
                <TextField
                  value={csvOutput}
                  multiline
                  rows={10}
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 2 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCopyToClipboard}
                  sx={{ mt: 2, mr: 2 }}
                >
                  Copy to clipboard
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConvertToJson}
                  sx={{ mt: 2, mr: 2 }}
                >
                  Convert to JSON
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConvertToXml}
                  sx={{ mt: 2, mr: 2 }}
                >
                  Convert to XML
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConvertToCsv}
                  sx={{ mt: 2, mr: 2 }}
                >
                  Return to CSV
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDownload}
                  sx={{ mt: 2, mr: 2 }}
                >
                  Download
                </Button>

              </>
            )}
          </>
        )}

        {/* Libraries Modal */}
        <Dialog
          open={openLibrariesModal}
          onClose={handleCloseLibrariesModal}
        >
          <DialogTitle>License info</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This tool uses the following open source libraries:
              <ul>
                <li>
                  <strong>React</strong> - A JavaScript library for building user
                  interfaces.{' '}
                  <a href="https://reactjs.org/" target="_blank" rel="noreferrer">
                    https://reactjs.org/
                  </a>
                </li>
                <li>
                  <strong>Material-UI (MUI)</strong> - React components for faster
                  and easier web development.{' '}
                  <a href="https://mui.com/" target="_blank" rel="noreferrer">
                    https://mui.com/
                  </a>
                </li>
                <li>
                  <strong>PapaParse</strong> - A powerful CSV parser that converts
                  CSV text input into JavaScript objects or arrays.{' '}
                  <a
                    href="https://www.papaparse.com/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://www.papaparse.com/
                  </a>
                </li>
                <li>
                  <strong>@hello-pangea/dnd</strong> - Beautiful and accessible
                  drag and drop for lists with React.{' '}
                  <a
                    href="https://github.com/hello-pangea/dnd"
                    target="_blank"
                    rel="noreferrer"
                  >
                    https://github.com/hello-pangea/dnd
                  </a>
                </li>
              </ul>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLibrariesModal}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Changelog Modal */}
        <Dialog open={openChangelogModal} onClose={handleCloseChangelogModal}>
          <DialogTitle>Changelog</DialogTitle>
          <DialogContent>
            <DialogContentText>
              <ul>
                <li>
                  <strong>Version 0.1B:</strong> Initial release with basic CSV
                  parsing and column reordering.
                </li>
                <li>
                  <strong>Version 0.2B:</strong> Added support for semicolon
                  delimiters and trimming spaces.
                </li>
                <li>
                  <strong>Version 0.3B:</strong> Added privacy notice and disclaimer.
                </li>
                <li>
                  <strong>Version 0.4B:</strong> Limited displayed records in the table, added instructions, and added a button to copy output to clipboard.
                </li>
                <li>
                  <strong>Version 0.5B:</strong> Added delete column, change output delimiter, and add column with default value features.
                </li>
                <li>
                  <strong>Version 0.6B:</strong> Added feature to change decimal sign (dot or comma) in the output.
                </li>
                <li>
                  <strong>Version 0.7B:</strong> Added load and download buttons.
                </li>
                <li>
                  <strong>Version 0.8B:</strong> Added feature to detect date columns and change their format in the output.
                </li>
                <li>
                  <strong>Version 0.9B:</strong> Changed "Delete Column" to "Hide Column" feature.
                </li>
                <li>
                  <strong>Version 1.0B:</strong> Bug report/Feedback button (TypeForm).
                </li>
                <li>
                  <strong>Version 1.1B:</strong> Added column sort feature.
                </li>
                <li>
                  <strong>Version 1.2B:</strong> Added a checkbox to automatically add field delimiters (quotes) to non-numeric fields (optional feature).
                </li>
                <li>
                  <strong>Version 1.3B:</strong> Added buttons to convert to XML and JSON.
                </li>
                <li>
                  <strong>Version 1.4B:</strong> Detect and display data types.
                </li>
              </ul>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseChangelogModal}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openXmlDialog} onClose={handleCloseXmlDialog}>
          <DialogTitle>Convert to XML</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please provide the XML root element and the record type.
            </DialogContentText>
            <TextField
              label="XML Root"
              fullWidth
              value={xmlRoot}
              onChange={(e) => setXmlRoot(e.target.value)}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Record Type"
              fullWidth
              value={recordType}
              onChange={(e) => setRecordType(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={generateXmlFromData} color="primary">
              Convert
            </Button>
            <Button onClick={handleCloseXmlDialog} color="secondary">
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openDataTypeDialog} onClose={() => setOpenDataTypeDialog(false)}>
          <DialogTitle>Select Data Type for {selectedColumn}</DialogTitle>
          <DialogContent>
            <Select
              value={columnDataTypes[selectedColumn] || 'text'}
              onChange={(e) => handleDataTypeSelect(selectedColumn, e.target.value)}
              fullWidth
            >
              <MenuItem value="string">String</MenuItem>
              <MenuItem value="integer">Integer</MenuItem>
              <MenuItem value="float">Float</MenuItem>
              <MenuItem value="date">Date</MenuItem>
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDataTypeDialog(false)}>Close</Button> {/* Close Button */}
          </DialogActions>
        </Dialog>

        <Dialog open={openDateFormatDialog} onClose={() => setOpenDateFormatDialog(false)}>
          <DialogTitle>Select Date Format for {selectedColumn}</DialogTitle>
          <DialogContent>
            <Select
              value={dateFormats[selectedColumn] || 'leave'}
              onChange={(e) => setDateFormats((prev) => ({
                ...prev,
                [selectedColumn]: e.target.value,
              }))}
              fullWidth
            >
              <MenuItem value="leave">Guess</MenuItem>
              <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
              <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
              <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
              <MenuItem value="MMM DD, YYYY">MMM DD, YYYY</MenuItem>
              <MenuItem value="DD-MM-YYYY">DD-MM-YYYY</MenuItem>
              <MenuItem value="DD MMM YYYY">DD MMM YYYY</MenuItem>
              <MenuItem value="MMMM DD, YYYY">MMMM DD, YYYY</MenuItem>
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDateFormatDialog(false)}>Close</Button> {/* Close Button */}
          </DialogActions>
        </Dialog>

      </Box>
    </Container>
  );
};

export default CSV;
